import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MainContainer } from "../../components/ContentWrapper";
import { ListPageHeader } from "../../components/ListPageHeader/ListPageHeader";
import { MultiStepOrderModal } from "./MultiStepOrderModal";
import OrderListItem from "./OrderListItem";
import {
    IconPencil,
    IconTrash,
    IconFeedbackForm,
    IconRefresh
} from "../../utils/icons";
import {
    Box,
    Button,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    Typography
} from "@mui/material";
import { ReactComponent as MoreOptionsIcon } from "../../assets/svg/more-options.svg";
import { OrderDownloadBtn } from "./OrderDownloadBtn";
import { DotLottleLoader, LoadMoreBtn, PageDivider } from "../../components/Loader";
import axiosInstant from "../../api";
import {
    getBrandOrders,
    getBrandOrdersByBrand,
    updateBrandOrder,
    orderDeleteImpact,
    getBrandSOWs,
    getBrandSOWsByBrand,
    setOrderVideosApproval,
    requestOrderFeedback,
    brandOrderFeedbackLink
} from "../../api/endpoints";
import { ConfirmationDialog } from "../../components/ConfirmationDialog/ConfirmationDialog";
import toast from "react-hot-toast";
import { copyPublicUrl, LinkGlyph } from "../../components/PublicUrlMenu";
import { MenuTooltip, STATUS_TOOLTIP_PROPS } from "../../components/DownloadMenu/MenuPopover";
import { ReactComponent as AddIcon } from "../../assets/svg/add-icon.svg";
import { ReactComponent as SOWIcon } from "../../assets/svg/sow-filter.svg";
import IconSearch from "../../assets/svg/search-icon-grey.svg";
import { useUser } from "../../hooks/useUser";
import { isInternal, isBrand, isRMN, isExpert, isAM } from "../../utils";
import { OrderPricingBreakdown } from "../../components/Pricing/OrderPricingBreakdown";
import { useCommonParams } from "../../hooks/useCommonParams";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import { BrandProject } from "../videoProduction/components/constants";
import { pdpImpactWarning, useDeleteImpact } from "../videoProduction/components/projectSoftDelete";
import { getBrandAllowedFileTypes } from "../admin/videos/selectedVideoCardV2/helpers";
import {
    formatProjectId,
    BRAND_PROJECT_STATUSES
} from "../videoProduction/helper";
import SortControls from "../../components/SortControls/SortControls";
import ProjectsSwitcher from "../videoProduction/components/ProjectsSwitcher";
import { sortData } from "../../utils/sortUtils";
import { usePagination } from "../../hooks/usePagination";
import { setVideosApproval } from "../../utils/bulkVideoApproval";
import { OrderRmnBrand } from "../../types/rmn";
import { usePortalPath } from '../../hooks/usePortalPath'
import { useWorkspaceBrandId } from '../../hooks/useWorkspaceBrandId';
import { DeliverablesTrackerModal, TrackerScope } from "../../components/DeliverablesTracker";

export interface OrderPricingBreakdownPayload {
    projectCount: number;
    listTotal?: number;
    savings?: number;
    effectiveDiscountPct?: number;
    projectCountDiscountPct?: number;
    adjustmentsTotal?: number;
    // Legacy headline tier % on older snapshots.
    discountPct?: number;
    projectIds: number[];
    total: number;
}

export interface BrandOrderItem {
    id: number;
    name: string;
    slack_channel?: string;
    download_page?: boolean;
    public_page_token?: string;
    feedback?: {
        token: string | null;
        requested: boolean;
        submitted: boolean;
        submitted_at?: string | null;
    };
    brand?: {
        id: number;
        name: string;
        logo?: string;
        cover_image?: string;
        rmn?: boolean;
        pre_approval_downloading?: boolean;
        enabled_file_types?: string[];
    };
    rmn_brand?: OrderRmnBrand | null;
    sow?: { id: number; name: string } | null;
    projects?: BrandProject[];
    pricing_total?: string | null;
    pricing_breakdown?: OrderPricingBreakdownPayload | null;
    pricing_snapshotted_at?: string | null;
}

const BRAND_LOGO_FALLBACK_TOKEN = "pk_fw3EgsMdRnKqF-72FUrrmQ";
const PAGE_SIZE = 20;

export function getBrandImageUrl(brand: BrandOrderItem["brand"]): string {
    const logo = brand?.logo ?? brand?.cover_image;
    if (logo && typeof logo === "string" && logo.trim() !== "") {
        return logo;
    }
    const name = brand?.name?.replace(/\s+/g, "") || "brand";
    return `https://img.logo.dev/${name}.com?token=${BRAND_LOGO_FALLBACK_TOKEN}`;
}

// The server's message for a failed request. control()-wrapped routes send a
// bare string body; typed errors with details send { error, message }. An HTML
// body (a proxy error page) is not a message.
const serverErrorMessage = (err: any, fallback: string): string => {
    const data = err?.response?.data;
    if (typeof data === "string" && data && !data.trimStart().startsWith("<")) return data;
    return data?.message || data?.error || err?.message || fallback;
};

// ?state=deleted: the admin-only Deleted view, a third ProjectsSwitcher
// segment beside Active / Completed (as on the Projects page).
const DELETED_STATE = "deleted";

const OrdersList: React.FC<{ showDeleted: boolean }> = ({ showDeleted }) => {
    const {
        searchParams,
        setSearchParams,
        searchText,
        setSearchText,
        page,
        setPage,
        sortType,
        resolvedSortKey,
        setSortDirection,
        getFilter,
        setFilter,
        hasAnyFilter,
        clearAllFilters
    } = useCommonParams({ sortKeyMap: { dateCreated: "id" } });

    const selectedSOW = useMemo(() => getFilter("sowId", ""), [searchParams]);
    const setSelectedSOW = (value: string) => setFilter("sowId", value);
    const isFilterApplied = hasAnyFilter(["sowId"]);

    const isActive = useMemo(() => getFilter("state", "active") !== "complete", [searchParams]);

    const handleClearFilters = () => {
        clearAllFilters(["sowId"]);
        setSearchText("");
    };

    const [showNewOrder, setShowNewOrder] = useState(searchParams.get("create") === "new");
    const [editModalKey, setEditModalKey] = useState(0);
    const [deleteOrder, setDeleteOrder] = useState<BrandOrderItem | null>(null);
    const [restoreOrder, setRestoreOrder] = useState<BrandOrderItem | null>(null);
    const deleteOrderPdpWarning = pdpImpactWarning(
        useDeleteImpact(deleteOrder ? orderDeleteImpact(deleteOrder.id) : null),
        "order"
    );
    // A delete or restore request in flight (one dialog is open at a time).
    const [submitting, setSubmitting] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; order: BrandOrderItem } | null>(null);
    const [trackerScope, setTrackerScope] = useState<TrackerScope | null>(null);
    const [sowOptions, setSowOptions] = useState<{ label: string; value: string }[]>([]);
    const { user, role } = useUser();

    // Deleting and restoring orders is admin / account manager admin only
    // (the API refuses anyone else), and so is the deleted-only list.
    const canManageDeletes = isAM(role);

    const switchView = (state: string) => {
        setSearchParams((prev) => {
            // Entering or leaving the Deleted view remounts the list: drop the
            // page, and any ?edit= / ?create= so no modal reopens over the
            // other list.
            if ((state === DELETED_STATE) !== showDeleted) {
                prev.delete("page");
                prev.delete("edit");
                prev.delete("create");
            }
            prev.set("state", state);
            return prev;
        });
    };

    const canDownloadOrder = (order: BrandOrderItem): boolean =>
        isInternal(role) ||
        !!order.brand?.pre_approval_downloading ||
        !!order.download_page ||
        !!order.projects?.some((p) => p.download_page);
    const navigate = useNavigate();
  const { p } = usePortalPath();
  // Own brand, or the opened workspace's brand for internal staff.
  const workspaceBrandId = useWorkspaceBrandId();
  const scopedBrandId = workspaceBrandId || user?.brand?.[0]?.id || 0;

    const handleSetAllOrderVideosApproval = (order: BrandOrderItem, approve: boolean) =>
        setVideosApproval({
            url: setOrderVideosApproval(order.id),
            approve,
            onDone: () => initLoad(page),
        });

    const isOrderFullyDelivered = (order: BrandOrderItem): boolean =>
        !!order.projects?.length &&
        order.projects.every(
            (p) => p.status === BRAND_PROJECT_STATUSES.FINAL_DELIVERED,
        );

    const FEEDBACK_REQUEST_ERRORS: Record<string, string> = {
        OrderNotFullyDelivered:
            "Every project on this order must be Final Delivered first.",
        OrderNotConfirmed: "Feedback can only be requested on confirmed orders.",
        OrderHasNoProjects: "This order has no projects yet.",
    };

    const feedbackIconTitle = (order: BrandOrderItem) => {
        if (!isInternal(role)) return "Give Feedback";
        if (order.feedback?.submitted) return "View Client Feedback";
        if (order.feedback?.requested) return "Copy Client Feedback Link (awaiting client)";
        return "Copy Client Feedback Link";
    };

    const handleOpenOwnFeedback = async (orderId: number) => {
        const tab = window.open("", "_blank");
        try {
            const { data } = await axiosInstant.get(brandOrderFeedbackLink(orderId));
            const path = `/orders/feedback/${data.token}`;
            if (tab) {
                tab.location.href = `${window.location.origin}${path}`;
            } else {
                navigate(path);
            }
        } catch (error: any) {
            if (tab) tab.close();
            const code = error?.response?.data?.error;
            toast.error(
                FEEDBACK_REQUEST_ERRORS[code] || "Could not open the feedback form.",
            );
        }
    };

    const handleCopyFeedbackLink = async (orderId: number) => {
        try {
            const { data } = await axiosInstant.post(requestOrderFeedback(orderId));
            const feedbackUrl = `${window.location.origin}/orders/feedback/${data.token}`;
            await navigator.clipboard.writeText(feedbackUrl);
            toast.success(
                data.submitted_at
                    ? "Feedback link copied. This order has already been submitted."
                    : "Feedback link copied to clipboard!",
            );
        } catch (error: any) {
            const code = error?.response?.data?.error;
            toast.error(
                FEEDBACK_REQUEST_ERRORS[code] || "Could not create a feedback link.",
            );
        }
    };

    const hasUnapprovedVideos = (order: BrandOrderItem) => {
        return order.projects?.some((p) => p.projectFiles?.some((f) => !f.final)) ?? false;
    };

    const hasApprovedVideos = (order: BrandOrderItem) => {
        return order.projects?.some((p) => p.projectFiles?.some((f) => f.final)) ?? false;
    };

    const handleOrderClick = (order: BrandOrderItem) => {
        // A deleted order's projects are deleted with it: nothing to open.
        if (showDeleted || !order.projects?.length) return;
        const tab = isActive ? "active" : "complete";
        navigate(p(`/video-production/projects?orderId=${order.id}&tab=${tab}`));
    };

    useEffect(() => {
        const fetchSOWs = async () => {
            try {
                let response;
                // scopedBrandId is the viewer's own brand, or the workspace
                // brand when internal staff have opened one (TDC-1609); 0 for
                // staff outside a workspace, who see every brand's SOWs.
                if (scopedBrandId) {
                    response = await axiosInstant.get(getBrandSOWsByBrand(scopedBrandId));
                } else {
                    response = await axiosInstant.get(getBrandSOWs());
                }
                const sows = response.data?.rows || response.data || [];
                setSowOptions(
                    sows.map((sow: any) => ({
                        label: sow.name,
                        value: String(sow.id),
                        badge: formatProjectId({
                            id: sow.id,
                            product: { title: sow.brand?.name || "" }
                        })
                    }))
                );
            } catch (e) {
                console.error("Failed to fetch SOWs:", e);
            }
        };
        fetchSOWs();
    }, [user]);

    const periodFilter = searchParams.get("period") || "";
    const fromFilter = searchParams.get("from") || "";
    const toFilter = searchParams.get("to") || "";

    const fetchFn = useCallback(
        async (pageNum: number) => {
            try {
                const offset = (pageNum - 1) * PAGE_SIZE;
                const params = new URLSearchParams();
                params.set("limit", String(PAGE_SIZE));
                params.set("offset", String(offset));
                if (searchText) params.set("search", searchText);
                if (selectedSOW) params.set("sow_id", selectedSOW);
                if (periodFilter) params.set("period", periodFilter);
                if (fromFilter) params.set("from", fromFilter);
                if (toFilter) params.set("to", toFilter);

                let endpoint: string;
                if (showDeleted) {
                    // Deleted orders only (admin / AM admin). Active vs
                    // completed does not apply: their projects are deleted
                    // too. The workspace brand rides as brand_id.
                    params.set("deleted", "1");
                    if (scopedBrandId) params.set("brand_id", String(scopedBrandId));
                    endpoint = getBrandOrders();
                } else {
                    params.set("state", isActive ? "active" : "complete");
                    if (scopedBrandId) {
                        endpoint = getBrandOrdersByBrand(scopedBrandId);
                    } else {
                        endpoint = getBrandOrders();
                    }
                }

                const response = await axiosInstant.get(`${endpoint}?${params.toString()}`);
                const { rows, count } = response.data;
                return { rows: rows ?? [], count: count ?? 0, error: null };
            } catch (e: any) {
                const msg = e?.response?.data?.error ?? "Failed to load orders";
                toast.error(msg);
                return { rows: [], count: 0, error: msg };
            }
        },
        [user?.brand, searchText, selectedSOW, periodFilter, fromFilter, toFilter, isActive, showDeleted]
    );

    const {
        items: orders,
        totalCount: count,
        loading,
        hasMore,
        canLoadPrevious,
        loadingPrevious,
        error,
        initLoad,
        loadMore,
        loadPrevious,
        topSentinelRef,
        editingItem: editingOrder,
        openEdit: openEditOrder,
        closeEdit: closeEditOrder
    } = usePagination<BrandOrderItem>({
        fetchFn,
        pageSize: PAGE_SIZE,
        deepLink: { page, setPage, searchParams, setSearchParams, ignoreParams: ["page", "edit"] },
        editDeepLink: { getId: (o) => o.id }
    });

    const sortedOrders = useMemo(() => {
        if (sortType.isAscending) {
            return sortData(orders, resolvedSortKey, true);
        }
        return orders;
    }, [orders, resolvedSortKey, sortType.isAscending]);

    return (
        <MainContainer>
            <ListPageHeader
                title="Orders"
                actionLabel={isBrand(role) || isRMN(user) ? "Request A Quote" : "New Order"}
                onActionClick={() => setShowNewOrder(true)}
                searchValue={searchText}
                onSearchChange={setSearchText}
                searchPlaceholder="Search Orders"
                searchInputId="search-orders"
                searchRightContent={
                    <>
                        {isFilterApplied && (
                            <button
                                type="button"
                                onClick={handleClearFilters}
                                className="text-sm text-accent-text font-medium border border-accent rounded-lg py-2 px-4 hover:bg-accent hover:text-white transition"
                            >
                                Clear Filters
                            </button>
                        )}
                        <SortControls isAscending={sortType.isAscending} onDirectionChange={setSortDirection} />
                        <CustomSelect
                            labelId="sow"
                            name="sow"
                            value={selectedSOW}
                            onChange={setSelectedSOW}
                            options={sowOptions}
                            placeholder="SOW"
                            showPlaceholderIcon
                            placeholderIcon={<SOWIcon />}
                            width={140}
                        />
                    </>
                }
            />

            <div className="mt-4">
                <ProjectsSwitcher
                    isActive={isActive}
                    isDeleted={showDeleted}
                    onActiveClick={() => switchView("active")}
                    onCompleteClick={() => switchView("complete")}
                    onDeletedClick={canManageDeletes ? () => switchView(DELETED_STATE) : undefined}
                    completeLabel="Completed"
                />
            </div>

            <PageDivider
                count={count}
                label={showDeleted ? "Deleted Orders" : "Orders"}
                className="mt-[24px] mb-[24px]"
                canLoadPrevious={canLoadPrevious}
                hasMore={hasMore}
                onLoadPrevious={loadPrevious}
            />

            {loading && orders.length === 0 && (
                <div className="flex justify-center py-12">
                    <DotLottleLoader fill={false} />
                </div>
            )}
            {error && <div className="py-4 text-red-600">{error}</div>}
            {!loading && !error && sortedOrders.length === 0 && showDeleted && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 20 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 450, color: "var(--text-2)" }}>
                        No deleted orders
                    </Typography>
                </Box>
            )}
            {!loading && !error && sortedOrders.length === 0 && !showDeleted && (
                <div className="flex flex-col items-center justify-center gap-6 mt-40 text-muted">
                    {/* stroke is an SVG attribute — it cannot read var(); inherit --muted via currentColor */}
                    <IconPencil size={64} stroke="currentColor" />
                    <div className="text-text-2 font-circular text-[14px] font-[450] leading-normal not-italic">
                        You have no active orders
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowNewOrder(true)}
                        className="flex items-center gap-1 bg-accent text-base text-white py-3 px-5 lg:px-10 cursor-pointer rounded-xl focus:outline-none focus:shadow-outline"
                    >
                        <AddIcon className="mr-1" />
                        {isBrand(role) || isRMN(user) ? "Request A Quote" : "New Order"}
                    </button>
                </div>
            )}
            {!loading && !error && sortedOrders.length > 0 && (
                <>
                    {canLoadPrevious && <div ref={topSentinelRef} style={{ height: 1 }} />}
                    {loadingPrevious && (
                        <div className="flex justify-center py-2">
                            <DotLottleLoader fill={false} sx={{ width: 100, height: 100 }} />
                        </div>
                    )}
                    <div className="flex flex-col gap-4">
                        {sortedOrders.map((order) => {
                            const uniqueContentTypes = (): string[] => {
                                if (!order.projects || order.projects.length === 0) return [];
                                const names = order.projects
                                    .map((p: BrandProject) => p.content_type?.name)
                                    .filter(Boolean);
                                return Array.from(new Set(names as string[]));
                            };
                            const uniqueChannelNames = (): string[] => {
                                if (!order.projects || order.projects.length === 0) return [];
                                const names = order.projects.flatMap((p: any) =>
                                    p.content_channels?.length
                                        ? p.content_channels.map((ch: any) => ch.name)
                                        : []
                                );
                                return Array.from(new Set(names.filter(Boolean) as string[]));
                            };
                            if (showDeleted) {
                                // Deleted rows: read-only, with Restore as the
                                // only action.
                                return (
                                    <div key={order.id}>
                                        <OrderListItem
                                            order={order}
                                            deleted
                                            contentTypes={uniqueContentTypes()}
                                            channelNames={uniqueChannelNames()}
                                            rightContent={
                                                <Box sx={{ display: "flex", gap: 1, alignItems: "center", pr: 2 }}>
                                                    {!isExpert(role) && (
                                                        <OrderPricingBreakdown
                                                            layout="tooltip"
                                                            pricingTotal={order.pricing_total}
                                                            pricingBreakdown={order.pricing_breakdown}
                                                            pricingSnapshottedAt={order.pricing_snapshotted_at}
                                                            order={order}
                                                        />
                                                    )}
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<IconRefresh size={14} />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setRestoreOrder(order);
                                                        }}
                                                        sx={{
                                                            textTransform: "none",
                                                            whiteSpace: "nowrap",
                                                            borderRadius: "8px",
                                                            fontSize: "14px",
                                                            fontWeight: 500,
                                                            color: "var(--accent-text)",
                                                            borderColor: "var(--border)",
                                                            backgroundColor: "var(--surface)",
                                                            "&:hover": {
                                                                borderColor: "var(--accent-text)",
                                                                backgroundColor: "var(--surface-2)",
                                                            },
                                                        }}
                                                    >
                                                        Restore Order
                                                    </Button>
                                                </Box>
                                            }
                                        />
                                    </div>
                                );
                            }
                            return (
                                <div key={order.id} onClick={() => handleOrderClick(order)} className="cursor-pointer">
                                    <OrderListItem
                                        order={order}
                                        contentTypes={uniqueContentTypes()}
                                        channelNames={uniqueChannelNames()}
                                        rightContent={
                                            <div className="flex gap-2 items-center pr-4">
                                                {!isExpert(role) && (
                                                    <OrderPricingBreakdown
                                                        layout="tooltip"
                                                        pricingTotal={order.pricing_total}
                                                        pricingBreakdown={order.pricing_breakdown}
                                                        pricingSnapshottedAt={order.pricing_snapshotted_at}
                                                        order={order}
                                                    />
                                                )}
                                                {order.id && (
                                                    <OrderDownloadBtn
                                                        orderId={order.id}
                                                        orderName={order.name}
                                                        commentCount={(order as any).comment_count}
                                                        disabled={!canDownloadOrder(order)}
                                                        allowedFileTypes={
                                                            isInternal(role)
                                                                ? null
                                                                : getBrandAllowedFileTypes(
                                                                      [order.rmn_brand, order.brand],
                                                                  )
                                                        }
                                                    />
                                                )}
                                                {order.download_page && order.id && (
                                                    <Tooltip
                                                        arrow
                                                        componentsProps={STATUS_TOOLTIP_PROPS}
                                                        title={
                                                            <MenuTooltip
                                                                title="Copy Public URL"
                                                                body="Copy the public order page link"
                                                            />
                                                        }
                                                    >
                                                    <button
                                                        type="button"
                                                        aria-label="Copy Public URL"
                                                        className="p-1 rounded-md cursor-pointer bg-surface text-muted border border-transparent hover:bg-surface-2 hover:border-muted"
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            const copied = await copyPublicUrl(
                                                                `${window.location.origin}/orders/public/${order.public_page_token}`,
                                                            );
                                                            if (copied) toast.success("Order link copied");
                                                            else toast.error("Could not copy the link");
                                                        }}
                                                    >
                                                        <LinkGlyph />
                                                    </button>
                                                    </Tooltip>
                                                )}
                                                {order.id && isOrderFullyDelivered(order) && (
                                                    <button
                                                        type="button"
                                                        className={`p-1 rounded-md cursor-pointer bg-surface border border-transparent hover:bg-surface-2 hover:border-muted ${
                                                            order.feedback?.submitted ? "text-up" : "text-muted"
                                                        }`}
                                                        title={feedbackIconTitle(order)}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!isInternal(role)) {
                                                                handleOpenOwnFeedback(order.id as number);
                                                                return;
                                                            }
                                                            const submittedToken =
                                                                order.feedback?.submitted && order.feedback.token;
                                                            if (submittedToken) {
                                                                window.open(
                                                                    `${window.location.origin}/orders/feedback/${submittedToken}`,
                                                                    "_blank",
                                                                    "noopener,noreferrer",
                                                                );
                                                            } else {
                                                                handleCopyFeedbackLink(order.id as number);
                                                            }
                                                        }}
                                                    >
                                                        <IconFeedbackForm width={18} height={18} stroke="currentColor" />
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="p-1 rounded-md cursor-pointer bg-surface text-muted border border-transparent hover:bg-surface-2 hover:border-muted"
                                                    title={isBrand(role) || isRMN(user) ? "Edit Request A Quote" : "Edit Order"}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openEditOrder(order);
                                                    }}
                                                >
                                                    <IconPencil stroke="currentColor" size={18} />
                                                </button>
                                                {canManageDeletes && (
                                                    <button
                                                        type="button"
                                                        className="p-1 rounded-md cursor-pointer bg-surface text-muted border border-transparent hover:bg-surface-2 hover:border-muted"
                                                        title="Delete Order"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setDeleteOrder(order);
                                                        }}
                                                    >
                                                        <IconTrash stroke="currentColor" width={18} height={18} />
                                                    </button>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setMenuAnchor({ el: e.currentTarget, order });
                                                    }}
                                                >
                                                    <MoreOptionsIcon />
                                                </IconButton>
                                            </div>
                                        }
                                    />
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {sortedOrders.length > 0 && hasMore && <LoadMoreBtn onClick={loadMore} loading={loading} />}

            <Menu anchorEl={menuAnchor?.el} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                <MenuItem
                    onClick={() => {
                        const o = menuAnchor?.order;
                        setMenuAnchor(null);
                        if (o) {
                            setEditModalKey((k) => k + 1);
                            openEditOrder(o);
                        }
                    }}
                >
                    {isBrand(role) || isRMN(user) ? "Edit Request A Quote" : "Edit Order"}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        const o = menuAnchor?.order;
                        setMenuAnchor(null);
                        if (o) handleSetAllOrderVideosApproval(o, true);
                    }}
                    disabled={!menuAnchor?.order || !hasUnapprovedVideos(menuAnchor?.order!)}
                >
                    Approve All Videos
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        const o = menuAnchor?.order;
                        setMenuAnchor(null);
                        if (o) handleSetAllOrderVideosApproval(o, false);
                    }}
                    disabled={!menuAnchor?.order || !hasApprovedVideos(menuAnchor?.order!)}
                >
                    Unapprove All Videos
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        const o = menuAnchor?.order;
                        setMenuAnchor(null);
                        if (o) setTrackerScope({ orderId: o.id });
                    }}
                >
                    Deliverables tracker
                </MenuItem>
                {canManageDeletes && (
                    <MenuItem
                        onClick={() => {
                            const o = menuAnchor?.order;
                            setMenuAnchor(null);
                            if (o) setDeleteOrder(o);
                        }}
                    >
                        Delete Order
                    </MenuItem>
                )}
            </Menu>

            <DeliverablesTrackerModal scope={trackerScope} onClose={() => setTrackerScope(null)} />

            {showNewOrder && (
                <MultiStepOrderModal
                    open={showNewOrder}
                    onClose={() => setShowNewOrder(false)}
                    onSuccess={() => initLoad(1)}
                    mode="create"
                />
            )}
            {editingOrder && !showDeleted && (
                <MultiStepOrderModal
                    key={`edit-order-${editingOrder.id}-${editModalKey}`}
                    open
                    onClose={closeEditOrder}
                    onSuccess={() => initLoad(page)}
                    mode="edit"
                    orderId={editingOrder.id}
                    initialOrderName={editingOrder.name}
                    initialBrand={editingOrder.brand || null}
                    initialSOW={editingOrder.sow || null}
                    initialSlackChannel={editingOrder.slack_channel || ""}
                    initialDownloadPage={editingOrder.download_page ?? false}
                    initialProjects={(editingOrder.projects || []).map((p) => ({
                        project_name: p.title,
                        project_id: p.id,
                        content_type: "",
                        channel: "",
                        channel_id: null,
                        product_name: "",
                        product_id: null,
                        download_page: p.download_page ?? false,
                        pricing_total: p.pricing_total,
                        production_adjustment: p.production_adjustment ?? null,
                        price_locked: p.price_locked ?? false,
                        status: p.status ?? null
                    }))}
                    initialPricingTotal={editingOrder.pricing_total}
                    initialPricingBreakdown={editingOrder.pricing_breakdown}
                    initialPricingSnapshottedAt={editingOrder.pricing_snapshotted_at}
                />
            )}
            {deleteOrder && (
                <ConfirmationDialog
                    open
                    title="Delete Order"
                    description={[
                        `Delete "${deleteOrder.name || "Untitled Order"}"? Its projects and their videos are removed with it.`,
                        ...(deleteOrderPdpWarning ? [deleteOrderPdpWarning] : []),
                        "An admin can restore it from the Deleted tab on the Orders page.",
                    ]}
                    confirmLabel="Delete"
                    disabled={submitting}
                    onClose={() => {
                        if (!submitting) setDeleteOrder(null);
                    }}
                    onConfirm={async () => {
                        setSubmitting(true);
                        try {
                            await axiosInstant.patch(updateBrandOrder(deleteOrder.id), { deactivated: true });
                            await initLoad(page);
                            toast.success("Order deleted successfully.");
                        } catch (err: any) {
                            toast.error(serverErrorMessage(err, "Failed to delete order"));
                        } finally {
                            setSubmitting(false);
                            setDeleteOrder(null);
                        }
                    }}
                />
            )}
            {restoreOrder && (
                <ConfirmationDialog
                    open
                    title="Restore Order"
                    description={[
                        `Restore "${restoreOrder.name || "Untitled Order"}"? The projects and videos deleted with it come back. Projects deleted on their own before it stay deleted.`,
                        "Projects that rejoin a live expert selection take its status. A project at Pre-Production through Video Review with no live selection to rejoin goes back to Scheduling, and its expert SOWs must be sent again. Other projects keep their status.",
                    ]}
                    confirmLabel="Restore"
                    disabled={submitting}
                    onClose={() => {
                        if (!submitting) setRestoreOrder(null);
                    }}
                    onConfirm={async () => {
                        setSubmitting(true);
                        try {
                            await axiosInstant.patch(updateBrandOrder(restoreOrder.id), { deactivated: false });
                            await initLoad(page);
                            toast.success("Order restored with the projects and videos deleted with it.");
                        } catch (err: any) {
                            toast.error(serverErrorMessage(err, "Failed to restore order"));
                        } finally {
                            setSubmitting(false);
                            setRestoreOrder(null);
                        }
                    }}
                />
            )}
        </MainContainer>
    );
};

// Each view gets its own list (a keyed remount). usePagination can't cancel a
// request, so a slow page of one view, or initLoad's walk back to page 1,
// would otherwise land in the other view's list: live orders tagged Deleted
// with Restore, or deleted ones with Edit / Delete / Approve.
const OrdersPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const { role } = useUser();
    // Anyone but an admin / AM admin with ?state=deleted gets the Active list.
    const showDeleted = isAM(role) && searchParams.get("state") === DELETED_STATE;
    return <OrdersList key={showDeleted ? "deleted" : "live"} showDeleted={showDeleted} />;
};

export default OrdersPage;
